import {useState, useMemo} from 'react'
import {useQuery} from '@tanstack/react-query'
import {FolderOpen, ChevronRight, ChevronDown, Globe, User, Search} from 'lucide-react'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Icon} from '@/components/shared/Icon'
import {SmallText} from '@/components/shared/SmallText'
import {Strong} from '@/components/shared/Strong'
import {Badge} from '@/components/ui/badge'
import {SearchClearButton} from '@/components/shared/SearchClearButton'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {IconInput} from '@/components/shared/IconInput'
import {IconText} from '@/components/shared/IconText'
import {MutedText} from '@/components/shared/MutedText'
import {ScrollableBox} from '@/components/shared/ScrollableBox'
import {IndentedPickerButton} from '@/components/shared/IndentedPickerButton'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import type {Page} from '@/types/page'
import {CategoryType, type ReportCategoryListItem, type CategoryTypeValue} from './types'

interface CategoryTreeNode extends ReportCategoryListItem {
    children: CategoryTreeNode[]
}

export function CategoryPickerDialog({open, onOpenChange, onSelect, title, description, typeFilter, excludeIds, highlightIds, showNoneOption}: {
    open: boolean
    onOpenChange: (open: boolean) => void
    onSelect: (categoryId: string | null, categoryName: string) => void
    title: string
    description?: string
    typeFilter?: CategoryTypeValue
    excludeIds?: Set<string>
    highlightIds?: Set<string>
    showNoneOption?: boolean
}) {
    const services = useServices()
    const i18n = useI18n()
    const [searchQuery, setSearchQuery] = useState('')
    const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set())
    const [selectedId, setSelectedId] = useState<string | null>(null)

    const {data: allCategoriesPage} = useQuery<Page<ReportCategoryListItem>>({
        queryKey: ['category-picker', typeFilter],
        queryFn: () => services.reporting.categories({
            size: 5000,
            ...(typeFilter ? {type: typeFilter} : {}),
        }) as Promise<Page<ReportCategoryListItem>>,
        enabled: open,
    })

    const allCategories = useMemo(() => allCategoriesPage?.content ?? [], [allCategoriesPage])

    // Expand highlightIds to include all ancestors (parent chain) so the full path is accented
    const effectiveHighlightIds = useMemo(() => {
        if (!highlightIds || highlightIds.size === 0) return undefined
        const parentMap = new Map<string, string | null>()
        for (const c of allCategories) parentMap.set(c.categoryId, c.parentCategoryId ?? null)
        const result = new Set(highlightIds)
        for (const id of highlightIds) {
            let current = parentMap.get(id) ?? null
            while (current) {
                result.add(current)
                current = parentMap.get(current) ?? null
            }
        }
        return result
    }, [highlightIds, allCategories])

    // Build tree from flat list, excluding specified IDs and their descendants
    const tree = useMemo(() => {
        const excluded = excludeIds ?? new Set<string>()
        const filtered = allCategories.filter(c => !excluded.has(c.categoryId))

        // Build parent→children map
        const childrenMap = new Map<string | null, ReportCategoryListItem[]>()
        for (const c of filtered) {
            const parentKey = c.parentCategoryId ?? null
            const list = childrenMap.get(parentKey) ?? []
            list.push(c)
            childrenMap.set(parentKey, list)
        }

        // Recursively build tree, excluding descendants of excluded IDs
        function buildNodes(parentId: string | null): CategoryTreeNode[] {
            const items = childrenMap.get(parentId) ?? []
            return items.map(item => ({
                ...item,
                children: buildNodes(item.categoryId),
            }))
        }

        return buildNodes(null)
    }, [allCategories, excludeIds])

    // Filter tree by search query — if a node matches, show it + all ancestors
    const filteredTree = useMemo(() => {
        if (!searchQuery.trim()) return tree

        const query = searchQuery.toLowerCase()

        function filterNodes(nodes: CategoryTreeNode[]): CategoryTreeNode[] {
            return nodes.reduce<CategoryTreeNode[]>((acc, node) => {
                const nameMatches = node.name.toLowerCase().includes(query)
                const filteredChildren = filterNodes(node.children)
                if (nameMatches || filteredChildren.length > 0) {
                    acc.push({...node, children: filteredChildren})
                }
                return acc
            }, [])
        }

        return filterNodes(tree)
    }, [tree, searchQuery])

    function toggleExpanded(id: string, e: React.MouseEvent) {
        e.stopPropagation()
        setExpandedIds(prev => {
            const next = new Set(prev)
            if (next.has(id)) next.delete(id)
            else next.add(id)
            return next
        })
    }

    function handleSelect(id: string | null) {
        setSelectedId(id)
    }

    function handleConfirm() {
        const selectedName = selectedId
            ? allCategories.find(c => c.categoryId === selectedId)?.name ?? ''
            : ''
        onSelect(selectedId, selectedName)
        onOpenChange(false)
        setSearchQuery('')
        setSelectedId(null)
        setExpandedIds(new Set())
    }

    function handleCancel() {
        onOpenChange(false)
        setSearchQuery('')
        setSelectedId(null)
        setExpandedIds(new Set())
    }

    // When search is active, auto-expand all nodes
    const effectiveExpandedIds = searchQuery.trim() ? new Set(allCategories.map(c => c.categoryId)) : expandedIds

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) handleCancel() }}>
            <DialogContent className="sm:max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{title}</DialogTitle>
                    {description && <DialogDescription>{description}</DialogDescription>}
                </DialogHeader>

                {/* Search input */}
                <IconInput
                    icon={Search}
                    inputSize="sm"
                    placeholder={i18n.common.search as string}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    trailing={searchQuery ? (
                        <SearchClearButton
                            data-testid="btn-clear-picker-search"
                            onClick={() => setSearchQuery('')}
                            iconClassName="h-3.5 w-3.5"
                        />
                    ) : undefined}
                />

                {/* Tree view */}
                <ScrollableBox autoHeight className="max-h-[400px]">
                    {showNoneOption && (
                        <PickerRow
                            label={i18n.reporting.noParent as string}
                            depth={0}
                            isSelected={selectedId === null}
                            onClick={() => handleSelect(null)}
                            isNoneOption
                        />
                    )}
                    {filteredTree.length === 0 && !showNoneOption && (
                        <InlineEmptyText>
                            {searchQuery ? i18n.reporting.noCategoriesMatch : i18n.reporting.noCategories}
                        </InlineEmptyText>
                    )}
                    {filteredTree.map(node => (
                        <TreeNode
                            key={node.categoryId}
                            node={node}
                            depth={0}
                            expandedIds={effectiveExpandedIds}
                            selectedId={selectedId}
                            highlightIds={effectiveHighlightIds}
                            onToggle={toggleExpanded}
                            onSelect={handleSelect}
                            i18n={i18n}
                        />
                    ))}
                </ScrollableBox>

                <SaveCancelFooter
                    testIdPrefix="picker"
                    onCancel={handleCancel}
                    disabled={!showNoneOption && selectedId === null}
                    saveLabel={i18n.common.select}
                    saveType="button"
                    onSave={handleConfirm}
                />
            </DialogContent>
        </Dialog>
    )
}

function TreeNode({node, depth, expandedIds, selectedId, highlightIds, onToggle, onSelect, i18n}: {
    node: CategoryTreeNode
    depth: number
    expandedIds: Set<string>
    selectedId: string | null
    highlightIds?: Set<string>
    onToggle: (id: string, e: React.MouseEvent) => void
    onSelect: (id: string) => void
    i18n: ReturnType<typeof useI18n>
}) {
    const isExpanded = expandedIds.has(node.categoryId)
    const hasChildren = node.children.length > 0

    return (
        <>
            <PickerRow
                label={node.name}
                depth={depth}
                isSelected={selectedId === node.categoryId}
                isHighlighted={highlightIds?.has(node.categoryId)}
                onClick={() => onSelect(node.categoryId)}
                type={node.type}
                reportCount={node.reportCount}
                hasChildren={hasChildren}
                isExpanded={isExpanded}
                onToggle={(e) => onToggle(node.categoryId, e)}
                i18n={i18n}
            />
            {isExpanded && node.children.map(child => (
                <TreeNode
                    key={child.categoryId}
                    node={child}
                    depth={depth + 1}
                    expandedIds={expandedIds}
                    selectedId={selectedId}
                    highlightIds={highlightIds}
                    onToggle={onToggle}
                    onSelect={onSelect}
                    i18n={i18n}
                />
            ))}
        </>
    )
}

function PickerRow({label, depth, isSelected, isHighlighted, onClick, isNoneOption, type, reportCount, hasChildren, isExpanded, onToggle, i18n}: {
    label: string
    depth: number
    isSelected: boolean
    isHighlighted?: boolean
    onClick: () => void
    isNoneOption?: boolean
    type?: CategoryTypeValue
    reportCount?: number
    hasChildren?: boolean
    isExpanded?: boolean
    onToggle?: (e: React.MouseEvent) => void
    i18n?: ReturnType<typeof useI18n>
}) {
    return (
        <IndentedPickerButton
            data-testid={`btn-picker-row-${isNoneOption ? 'none' : label}`}
            depth={depth}
            onClick={onClick}
            className={isSelected
                ? 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors bg-primary/10 border-l-2 border-l-primary'
                : isHighlighted
                    ? 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors bg-muted/30 border-l-2 border-l-muted-foreground/50'
                    : 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-muted/50 transition-colors border-l-2 border-l-transparent'}
        >
            {/* Chevron for expandable nodes */}
            {!isNoneOption && hasChildren && (
                <Icon
                    icon={isExpanded ? ChevronDown : ChevronRight}
                    size="sm"
                    muted
                    onClick={onToggle}
                />
            )}
            {!isNoneOption && !hasChildren && (
                <SmallText className="w-3.5 shrink-0">{' '}</SmallText>
            )}

            <Icon icon={FolderOpen} size="sm" muted/>
            <Strong truncate className="flex-1">{label}</Strong>

            {/* Report count */}
            {reportCount !== undefined && reportCount > 0 && (
                <MutedText size="xs" align="right" className="shrink-0 w-6">
                    {reportCount}
                </MutedText>
            )}

            {/* Type badge */}
            {type && i18n && (
                <Badge variant="outline" className="min-w-[72px]">
                    <IconText
                        icon={type === CategoryType.GLOBAL ? Globe : User}
                        size="xs"
                        iconClassName="h-2.5 w-2.5"
                    >
                        {type === CategoryType.GLOBAL ? i18n.reporting.typeGlobal : i18n.reporting.typePersonal}
                    </IconText>
                </Badge>
            )}
        </IndentedPickerButton>
    )
}
